import { useCallback, useEffect, useRef, useState } from 'react';
import styles from './styles.module.scss';
import TableSection from '@sections/transactions/table';
import classNames from 'classnames/bind';
import FilterSection from '@sections/transactions/filter';
import { useApiQuery } from '@hooks/api/useApiQuery';
import { apiTransactionsService } from '@api';
import {
    ETransactionAction,
    ETransactionType,
    TTransactionLite,
    TTransactionsFilter
} from '@xeppt/xeppt-sdk/types/transaction';
import { motion } from 'framer-motion';
import { pageAnimation } from '@const/animation';
import { ENewNotificationType } from '@enum';
import { routes } from '@const/routes';
import Button from '@components/common/button';
import { useIsFirstRender } from '@mantine/hooks';
import { TTransaction } from '@xeppt/xeppt-sdk/types';
import { modalIds } from '@const/modals';
import useModalContext from '@hooks/context/useModalContext';
import { useQueryParams } from '@hooks/helpers/useQueryParams';
import { useNavigate } from 'react-router-dom';
import { useNavigationContext } from '@hooks/context/useNavigationContext';
import { useLocales } from '@hooks/helpers/useLocales';
import Typography from '@components/common/typography';
import { TPaymentIntent } from '@xeppt/xeppt-sdk/types/payment-intent';

const cx = classNames.bind(styles);

const TransactionsLayout = () => {
    const [filter, setFilter] = useState<TTransactionsFilter>({
        action: undefined,
        date_from: undefined,
        date_to: undefined
    });
    const navigate = useNavigate();
    const [id, intent] = useQueryParams(['id', 'intent']);
    const { onOpen, changeModalData } = useModalContext();
    const isFirstRender = useIsFirstRender();
    const [activeTab, setActiveTab] = useState<'transactions' | 'requests'>('transactions');
    const [cursor, setCursor] = useState<Date>();
    const [intentCursor, setIntentCursor] = useState<Date>();
    const loader = useRef(null);
    const [transactions, setTransactions] = useState<TTransactionLite[]>([]);
    const [intents, setIntents] = useState<TPaymentIntent[]>([]);
    const [trigger, setTrigger] = useState(0);
    const { handleBack } = useNavigationContext();
    const [triggerClearFilter, setTriggerClearFilters] = useState(0);
    const { submitLocale } = useLocales();
    const [isOpen, setIsOpen] = useState(false);

    const {
        data: intentsResponse,
        isLoading: isIntentsLoading,
        handleRequest: refetchIntents
    } = useApiQuery({
        isInitialRequest: false,
        method: (request: any) =>
            apiTransactionsService.getPaymentIntents({
                cursor: request?.isNew
                    ? request?.date_to || new Date()
                    : intentCursor || new Date(),
                limit: 100,
                type: [ETransactionType.DEBIT]
            }),
        condition: activeTab === 'requests',
        deps: [activeTab],
        onSuccess: (data, requestData) => {
            if (!requestData?.isNew) {
                setIntents((state) => [...state, ...data.data]);
                setIntentCursor(data.cursor);
            } else {
                setIntents(data?.data || []);
                setIntentCursor(data.cursor);
            }
        },
        notificationType: ENewNotificationType.TRANSACTION
    });
    const {
        data: transactionsResponse,
        isLoading: isTransactionsLoading,
        handleRequest: handleGetTransactions
    } = useApiQuery({
        isInitialRequest: false,
        method: (request: any) => {
            return apiTransactionsService.findTransactions(
                {
                    date_to: request?.date_to,
                    date_from: request?.date_from,
                    action: request?.action?.length === 0 ? undefined : request?.action,
                    method: request?.method?.length === 0 ? undefined : request?.method,
                    type: request?.type?.length === 0 ? undefined : request?.type
                },
                {
                    cursor: request?.isNew ? request?.date_to : cursor,
                    limit: 100
                }
            );
        },
        condition: activeTab === 'transactions',
        onSuccess: (data, requestData) => {
            if (!requestData?.isNew) {
                setTransactions((state) => [...state, ...data.data]);
                setCursor(data.cursor);
            } else {
                setTransactions(data?.data || []);
                setCursor(data.cursor);
            }
        },
        notificationType: ENewNotificationType.TRANSACTION
    });

    useEffect(() => {
        const loadItems = () => {
            if (
                activeTab === 'transactions' &&
                transactions.length < (transactionsResponse?.total || 0) &&
                !isTransactionsLoading
            ) {
                handleGetTransactions();
            }
            if (
                activeTab === 'requests' &&
                intents.length < (intentsResponse?.total || 0) &&
                !isIntentsLoading
            ) {
                refetchIntents();
            }
        };

        loadItems();
    }, [trigger]);

    const handleObserver = useCallback((entries: any) => {
        const target = entries[0];
        if (target.isIntersecting) {
            setTrigger((prev) => prev + 1);
        }
    }, []);

    useEffect(() => {
        if (!isFirstRender) {
            if (activeTab === 'requests') {
                refetchIntents({ isNew: true });
            } else {
                handleGetTransactions({ ...filter, isNew: true });
            }
        }
    }, [activeTab]);

    useEffect(() => {
        const option = {
            root: null,
            rootMargin: '20px',
            threshold: 0
        };
        const observer = new IntersectionObserver(handleObserver, option);
        if (loader.current) {
            observer.observe(loader.current);
        }
    }, [handleObserver]);

    const handleViewTransaction = (transaction: TTransaction) => {
        changeModalData({
            transaction,
            refetchTransactions: () => handleGetTransactions({ ...filter, isNew: true })
        });
        onOpen({
            modalId: modalIds.VIEW_TRANSACTION,
            onClose: () => {
                if (id) {
                    navigate(routes.transactions);
                }
            }
        });
    };

    const handleViewIntent = (transaction: TPaymentIntent) => {
        changeModalData({
            transaction,
            refetchTransactions: () => refetchIntents({ ...filter, isNew: true })
        });
        onOpen({
            modalId: modalIds.VIEW_EXTERNAL_TRANSACTION,
            onClose: () => {
                if (intent) {
                    navigate(routes.transactions);
                }
            }
        });
    };

    useApiQuery({
        method: () => apiTransactionsService.getTransactionById(id || ''),
        condition: !!id,
        deps: [id],
        onSuccess: (data) => {
            handleViewTransaction(data);
        }
    });

    useApiQuery({
        method: () => apiTransactionsService.getPaymentIntentById(id || ''),
        condition: !!intent,
        deps: [intent],
        onSuccess: (data) => {
            handleViewIntent(data);
        }
    });

    return (
        <motion.div {...pageAnimation}>
            <div className={styles.wrapper}>
                <div className={cx([styles.left, { isOpen }])}>
                    <div className={styles.header}>
                        <Button
                            className={styles.back}
                            leftIcon="arrow_left"
                            variant="text-gray"
                            onClick={() => handleBack(routes.dashboard)}>
                            {submitLocale('back')}
                        </Button>
                        <Typography variant="h4">Transactions</Typography>
                    </div>
                    <TableSection
                        triggerClearFilter={() => setTriggerClearFilters((prev) => prev + 1)}
                        transactions={activeTab === 'requests' ? intents : transactions}
                        toggleFilter={() => setIsOpen((state) => !state)}
                        isTransactionsLoading={isTransactionsLoading || isIntentsLoading}
                        onChangeTab={(val) => setActiveTab(val)}
                        activeTab={activeTab}
                        filter={filter}
                        refetchTransactions={() => {
                            if (activeTab === 'requests') {
                                refetchIntents({ isNew: true });
                            } else {
                                handleGetTransactions({ ...filter, isNew: true });
                            }
                        }}
                        onChangeFilter={(value) => {
                            setCursor(undefined);
                            setIntentCursor(undefined);
                            setTransactions([]);
                            setIntents([]);
                            setFilter(value);
                            if (activeTab === 'transactions') {
                                handleGetTransactions({ ...value, isNew: true });
                            } else {
                                refetchIntents({ ...value, isNew: true });
                            }
                        }}
                    />
                    <div ref={loader} />
                </div>
                {isOpen && <div className={styles.overlay} onClick={() => setIsOpen(false)} />}
                <div className={cx([styles.right, { isOpen }])}>
                    <FilterSection
                        activeTab={activeTab}
                        toggleFilter={() => setIsOpen((state) => !state)}
                        triggerClearFilter={triggerClearFilter}
                        onChangeFilter={(value) => {
                            setCursor(undefined);
                            setIntentCursor(undefined);
                            setTransactions([]);
                            setIntents([]);
                            setFilter(value);
                            if (activeTab === 'transactions') {
                                handleGetTransactions({ ...value, isNew: true });
                            } else {
                                refetchIntents({ ...value, isNew: true });
                            }
                        }}
                    />
                </div>
            </div>
        </motion.div>
    );
};

export default TransactionsLayout;
